import { createRouter, createWebHistory } from 'vue-router'
import { authGuard } from './authGuard'
import logger from '@/utils/logger'

const routes = [
  {
    path: '/',
    name: 'Dashboard',
    component: () => import('../views/Dashboard.vue')
  },
  {
    path: '/bookings',
    name: 'Bookings',
    component: () => import('../views/Bookings.vue')
  },
  {
    path: '/bookings/:id',
    name: 'BookingDetails',
    component: () => import('../views/BookingDetails.vue')
  },
  {
    path: '/calendar',
    name: 'BookingCalendar',
    component: () => import('../views/BookingCalendar.vue')
  },
  {
    path: '/ai-recommendations',
    name: 'AIAllocationRecommendations',
    component: () => import('../views/AIAllocationRecommendations.vue')
  },
  {
    path: '/customers',
    name: 'Customers',
    component: () => import('../views/Customers.vue')
  },
  {
    path: '/customers/:id',
    name: 'CustomerProfile',
    component: () => import('../views/CustomerProfile.vue')
  },
  {
    path: '/leads',
    name: 'Leads',
    component: () => import('../views/Leads.vue')
  },
  {
    path: '/recalls',
    name: 'Recalls',
    component: () => import('../views/Recalls.vue')
  },
  {
    path: '/conversations',
    name: 'Conversations',
    component: () => import('../views/Conversations.vue')
  },
  {
    path: '/ai-calls',
    name: 'AICalls',
    component: () => import('../views/AICalls.vue')
  },
  {
    path: '/quotes',
    name: 'Quotes',
    component: () => import('../views/Quotes.vue')
  },
  {
    path: '/receipts',
    name: 'Receipts',
    component: () => import('../views/Receipts.vue')
  },
  {
    path: '/commercial-reports',
    name: 'CommercialReports',
    component: () => import('../views/CommercialReports.vue')
  },
  {
    path: '/inspection-reports',
    name: 'InspectionReports',
    component: () => import('../views/InspectionReportsPage.vue')
  },
  {
    path: '/inspection-reports/:inspectionId',
    name: 'InspectionReview',
    component: () => import('../views/InspectionReviewPage.vue')
  },
  {
    path: '/technicians',
    name: 'Technicians',
    component: () => import('../views/Technicians.vue'),
    meta: { requiresSuperAdmin: true }
  },
  {
    path: '/technicians/create',
    name: 'TechnicianCreate',
    component: () => import('../views/TechnicianCreate.vue'),
    meta: { requiresSuperAdmin: true }
  },
  {
    path: '/technicians/:id',
    name: 'TechnicianProfile',
    component: () => import('../views/TechnicianProfile.vue'),
    meta: { requiresSuperAdmin: true }
  },
  {
    path: '/vehicles',
    name: 'Vehicles',
    component: () => import('../views/Vehicles.vue'),
    meta: { requiresSuperAdmin: true }
  },
  {
    path: '/timesheets',
    name: 'Timesheets',
    component: () => import('../views/Timesheets.vue')
  },
  {
    path: '/payments',
    name: 'Payments',
    component: () => import('../views/Payments.vue')
  },
  {
    path: '/payments/:id',
    name: 'PaymentDetails',
    component: () => import('../views/PaymentDetails.vue')
  },
  {
    path: '/payroll-report',
    name: 'PayrollReport',
    component: () => import('../views/PayrollReport.vue')
  },
  {
    path: '/invoices',
    name: 'Invoices',
    component: () => import('../views/Invoices.vue')
  },
  {
    path: '/reports',
    name: 'Reports',
    component: () => import('../views/Reports.vue')
  },
  {
    path: '/operations-report',
    name: 'OperationsReport',
    component: () => import('../views/OperationsReport.vue')
  },
  {
    path: '/booking-insights',
    name: 'BookingInsights',
    component: () => import('../views/BookingInsights.vue'),
    meta: { requiresSuperAdmin: true }
  },
  {
    path: '/returning-customers',
    name: 'ReturningCustomers',
    component: () => import('../views/ReturningCustomersPage.vue')
  },
  {
    path: '/user-management',
    name: 'UserManagement',
    component: () => import('../views/UserManagement.vue'),
    meta: { requiresSuperAdmin: true }
  },
  {
    path: '/booking-slots',
    name: 'BookingSlots',
    component: () => import('../views/BookingSlots.vue')
  },
  {
    path: '/booking-locks',
    name: 'BookingLocks',
    component: () => import('../views/BookingLocks.vue')
  },
  {
    path: '/time-slots',
    name: 'TimeSlots',
    component: () => import('../views/TimeSlots.vue')
  },
  // {
  //   path: '/notifications',
  //   name: 'Notifications',
  //   component: () => import('../views/Notifications.vue')
  // },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/Login.vue'),
    meta: { hideLayout: true, allowAnonymous: true }
  },
  {
    path: '/callback',
    name: 'Callback',
    component: () => import('../views/Callback.vue'),
    meta: { hideLayout: true, allowAnonymous: true }
  },
  {
    path: '/access-denied',
    name: 'AccessDenied',
    component: () => import('../views/AccessDenied.vue'),
    meta: { hideLayout: true, allowAnonymous: true }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guard for authentication and logging
router.beforeEach((to, from, next) => {
  // Log navigation attempt
  logger.navigation(from, to, {
    params: to.params,
    query: to.query,
    meta: to.meta,
    isAnonymous: !!to.meta.allowAnonymous
  })

  // Performance tracking - start navigation timer
  const navigationStart = Date.now()
  to.meta.navigationStart = navigationStart

  // Allow routes with allowAnonymous meta
  if (to.meta.allowAnonymous) {
    logger.debug('Allowing anonymous access to route', {
      route: to.name,
      path: to.path
    })
    return next()
  }
  
  // Use Auth0 guard for protected routes
  logger.debug('Checking authentication for protected route', {
    route: to.name,
    path: to.path
  })
  return authGuard(to, from, next)
})

// After navigation completes
router.afterEach((to, from, failure) => {
  // Calculate navigation duration
  const duration = to.meta.navigationStart ? Date.now() - to.meta.navigationStart : null

  if (failure) {
    logger.error('Navigation failed', failure, {
      to: { name: to.name, path: to.path },
      from: { name: from.name, path: from.path },
      duration
    })
  } else {
    logger.info('Navigation completed', {
      to: { name: to.name, path: to.path },
      from: { name: from.name, path: from.path },
      duration,
      params: to.params,
      query: to.query
    })

    // Log slow navigation
    if (duration && duration > 1000) {
      logger.warn('Slow navigation detected', {
        route: to.name,
        path: to.path,
        duration
      })
    }

    // Track page views
    logger.userAction('page_view', {
      route: to.name,
      path: to.path,
      params: to.params,
      query: to.query,
      referrer: from.name
    })
  }

  // Clean up navigation metadata
  delete to.meta.navigationStart
})

export default router
